import { inject, Injectable } from '@angular/core';
import { environment } from '../../../../environments/environment';
import { HttpClient, HttpParams } from '@angular/common/http';
import { from, Observable, switchMap } from 'rxjs';
import { BaseQueryParams, Ac, Committee, CommitteeHistory, IResponse, IResponsePaginate, WithMetadata, User } from '@/types';
import { CommitteeDto } from '@/types/dtos.types';
import { AuthorizationService } from '@/services/authorization.service';

export interface CommitteeQuery extends BaseQueryParams {
  nom?: string;
  decretCreation?: string;
  responsable?: string;
  special?: boolean;
  ancrage?: string;
  adresse?: string;
  membres?: string;
}

@Injectable({
  providedIn: 'root',
})
export class CommitteeService {
  BASEURL = `${environment.api.paramsUrl}/com`;
  httpClient = inject(HttpClient);

  authService = inject(AuthorizationService);

  getCommittees(params?: CommitteeQuery): Observable<IResponsePaginate<Committee>> {
    return this.httpClient.get<IResponsePaginate<Committee>>(`${this.BASEURL}`, { params: params as HttpParams });
  }

  getCommitteesList(params?: CommitteeQuery): Observable<IResponse<Committee>> {
    return this.httpClient.get<IResponse<Committee>>(`${this.BASEURL}/list`, { params: params as HttpParams });
  }

  getOneCommittee(id: number): Observable<WithMetadata<Committee>> {
    return this.httpClient.get<WithMetadata<Committee>>(`${this.BASEURL}/batch/${id}`);
  }

  getUserCommittees(userId?: string): Observable<IResponse<Committee>> {
    return this.httpClient.get<IResponse<Committee>>(`${this.BASEURL}/user/${userId}/coms`);
  }

  getCurrentUserCommittees(): Observable<IResponse<Committee>> {
    return from(this.authService.getUserId()).pipe(switchMap(userId => this.getUserCommittees(userId)));
  }

  getCpmpCommittees(): Observable<IResponse<Committee>> {
    return this.httpClient.get<IResponse<Committee>>(`${this.BASEURL}/type/cpmp`);
  }

  getCncmpCommittees(): Observable<IResponse<Committee>> {
    return this.httpClient.get<IResponse<Committee>>(`${this.BASEURL}/type/cncmp`);
  }

  addCommittee(committee: CommitteeDto): Observable<WithMetadata<Committee>> {
    const formData = new FormData();
    formData.append('nom', committee.nom);
    if (committee.abreviation) {
      formData.append('abreviation', committee.abreviation);
    }
    if (committee.nomAr) {
      formData.append('nomAr', committee.nomAr);
    }
    formData.append('decretCreation', committee.decretCreation || '');
    formData.append('special', committee.special.toString());
    formData.append('ancrage', '');
    formData.append('contact', '');
    formData.append('adresse', '');
    formData.append('comTypeId', committee.comTypeId.toString());
    formData.append('responsableId', committee.responsableId);
    formData.append('tutelId', committee.tutelId ? committee.tutelId.toString() : '');
    formData.append('logo', ''); // Add logo field as shown in Postman
    formData.append('theAdresse', JSON.stringify(committee.theAdresse));
    if (committee.docIds && committee.docIds.length > 0) {
      committee.docIds.forEach((id, idx) => formData.append(`docIds[${idx}]`, id.toString()));
    } else {
      formData.append('docIds', '');
    }
    formData.append('userIds', committee.userIds && committee.userIds.length > 0 ? committee.userIds[0].toString() : '');

    if (committee.consellierIds && committee.consellierIds.length > 0) {
      committee.consellierIds.forEach((id, idx) => formData.append(`consellierIds[${idx}]`, id.toString()));
    } else {
      formData.append('consellierIds', '');
    }

    // Add files as files[] object only if there are files
    if (committee.files && committee.files.length > 0) {
      committee.files.forEach((file: any) => {
        if (file instanceof File) {
          formData.append('files[]', file);
        } else if (typeof file === 'string') {
          // If it's a file path or URL, append as string
          formData.append('files[]', file);
        }
      });
    }
    // Don't append files[] field if there are no files

    return this.httpClient.post<WithMetadata<Committee>>(`${this.BASEURL}`, formData);
  }

  addCommitteeWithParams(params: HttpParams): Observable<WithMetadata<Committee>> {
    return this.httpClient.post<WithMetadata<Committee>>(`${this.BASEURL}`, null, { params });
  }

  updateCommittee(id: number, committee: CommitteeDto | FormData): Observable<IResponse<Committee>> {
    let formData: FormData;
    if (committee instanceof FormData) {
      formData = committee;
    } else {
      formData = new FormData();
      formData.append('nom', committee.nom);
      if (committee.abreviation) {
        formData.append('abreviation', committee.abreviation);
      }
      if (committee.nomAr) {
        formData.append('nomAr', committee.nomAr);
      }
      formData.append('decretCreation', committee.decretCreation || '');
      formData.append('special', committee.special.toString());
      formData.append('ancrage', ''); // Set to empty string as requested
      formData.append('contact', ''); // Set to empty string as requested
      formData.append('adresse', 'a'); // Set to empty string as requested
      formData.append('comTypeId', committee.comTypeId.toString());
      formData.append('responsableId', committee.responsableId);
      formData.append('tutelId', committee.tutelId ? committee.tutelId.toString() : '');
      formData.append('logo', ''); // Add logo field as shown in Postman
      formData.append('theAdresse', JSON.stringify(committee.theAdresse));
      if (committee.docIds && committee.docIds.length > 0) {
        committee.docIds.forEach((id, idx) => formData.append(`docIds[${idx}]`, id.toString()));
      } else {
        formData.append('docIds', '');
      }
      if (committee.userIds && committee.userIds.length > 0) {
        committee.userIds.forEach((id) => formData.append(`userIds`, id.toString()));
      } else {
        formData.append('userIds', '');
      }
      if (committee.consellierIds && committee.consellierIds.length > 0) {
        committee.consellierIds.forEach((id) => formData.append(`consellierIds`, id.toString()));
      } else {
        formData.append('consellierIds', '');
      }
      if (committee.files && committee.files.length > 0) {
        committee.files.forEach((file: any) => {
          if (file instanceof File) {
            formData.append('files[]', file);
          } else if (typeof file === 'string') {
            formData.append('files[]', file);
          }
        });
      }
    }
    return this.httpClient.put<IResponse<Committee>>(`${this.BASEURL}/${id}`, formData);
  }

  updateCommitteeWithParams(id: number, params: HttpParams): Observable<IResponse<Committee>> {
    return this.httpClient.put<IResponse<Committee>>(`${this.BASEURL}/${id}`, null, { params });
  }

  deleteCommittee(id: number): Observable<IResponse<Committee>> {
    return this.httpClient.delete<IResponse<Committee>>(`${this.BASEURL}/${id}`);
  }

  downloadDocument(alfrescoId: string): Observable<Blob> {
    return this.httpClient.post(`${environment.api.paramsUrl}/docs/download/com`, { id: alfrescoId }, { responseType: 'blob' });
  }

  getTutels(): Observable<Ac[]> {
    return this.httpClient.get<IResponse<Ac>>(`${environment.api.paramsUrl}/ac/is_tutel`);
  }
  getTutelsWithoutCom(typeComId: number, comId?: number): Observable<Ac[]> {
    const params: any = { typeComId };
    if (comId !== undefined && comId !== null) {
      params.comId = comId;
    }
    return this.httpClient.get<Ac[]>(`${environment.api.paramsUrl}/ac/acs-tutel-list-with-no-com-cpmp`, { params });
  }

  getAllSpecialCommittees(): Observable<IResponse<Committee>> {
    return this.httpClient.get<IResponse<Committee>>(`${this.BASEURL}/all_special`);
  }

  getCommitteeHistory(comId: number, params?: BaseQueryParams, exactDate?: string): Observable<IResponsePaginate<CommitteeHistory>> {
    let queryParams = new HttpParams();
    if (exactDate) {
      queryParams = queryParams.set('exactDate', exactDate);
    }
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams = queryParams.set(key, value.toString());
        }
      });
    }
    return this.httpClient.get<IResponsePaginate<CommitteeHistory>>(`${this.BASEURL}/users/historique-by-com/${comId}`, { params: queryParams });
  }

  getConsellierHistory(comId: number, params?: BaseQueryParams, exactDate?: string): Observable<IResponsePaginate<any>> {
    let queryParams = new HttpParams();
    if (exactDate) {
      queryParams = queryParams.set('exactDate', exactDate);
    }
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams = queryParams.set(key, value.toString());
        }
      });
    }
    return this.httpClient.get<IResponsePaginate<any>>(`${this.BASEURL}/conselliers/historique-by-com/${comId}`, { params: queryParams });
  }

  getCpmpMembers(acId?: number): Observable<User[]> {
    return this.httpClient.get<User[]>(`${this.BASEURL}/cpmp-members/${acId}`);
  }

  getComMembers(comId?: number): Observable<User[]> {
    return this.httpClient.get<User[]>(`${this.BASEURL}/com-members/${comId}`);
  }
}
